import { useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Settings, VolumeX, Loader2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { usePersonalMutes, type PersonalMute } from './hooks/use-personal-mutes';
import { useToast } from '@/hooks/use-toast';

export function PersonalMutesPanel() {
  const { toast } = useToast();
  const {
    mutedUsers,
    mutesLoading,
    unmuteUser,
    isUnmuting,
  } = usePersonalMutes();

  const handleUnmute = async (userId: string, username: string | null) => {
    try {
      await unmuteUser(userId);
      toast({
        title: 'User unmuted',
        description: `${username || 'User'} has been unmuted. You will now see their messages.`,
      });
    } catch (error) {
      toast({
        title: 'Failed to unmute',
        description: 'Please try again',
        variant: 'destructive',
      });
    }
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Settings className="h-5 w-5" />
          {mutedUsers.length > 0 && (
            <span className="absolute -top-1 -right-1 h-4 w-4 bg-muted-foreground text-background text-xs rounded-full flex items-center justify-center">
              {mutedUsers.length > 9 ? '9+' : mutedUsers.length}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[400px] sm:w-[540px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <VolumeX className="h-5 w-5" />
            Muted Users
          </SheetTitle>
          <SheetDescription>
            Users you've muted won't appear in chat. This applies across all leagues.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6">
          {mutesLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : mutedUsers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <VolumeX className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>You haven't muted anyone</p>
              <p className="text-sm mt-1">
                Mute users from the chat message menu to hide their messages.
              </p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              {mutedUsers.map((mute) => (
                <MutedUserCard
                  key={mute.id}
                  mute={mute}
                  onUnmute={() => handleUnmute(mute.mutedId, mute.username)}
                  isUnmuting={isUnmuting}
                />
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

interface MutedUserCardProps {
  mute: PersonalMute;
  onUnmute: () => void;
  isUnmuting: boolean;
}

function MutedUserCard({ mute, onUnmute, isUnmuting }: MutedUserCardProps) {
  const mutedDate = mute.createdAt
    ? formatDistanceToNow(new Date(mute.createdAt), { addSuffix: true })
    : 'Unknown';

  return (
    <Card>
      <CardHeader className="py-3 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Avatar className="h-8 w-8">
              <AvatarImage src={mute.avatarImageUrl || undefined} />
              <AvatarFallback>
                {(mute.username || 'U').charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <CardTitle className="text-sm font-medium">
                {mute.username || 'Unknown User'}
              </CardTitle>
              <CardDescription className="text-xs">
                Muted {mutedDate}
              </CardDescription>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onUnmute}
            disabled={isUnmuting}
          >
            {isUnmuting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              'Unmute'
            )}
          </Button>
        </div>
      </CardHeader>
    </Card>
  );
}
